import { Button, Field, FieldError, Textarea } from "@/components/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import type * as fhirTypes from "fhir/r4";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";
import { editSubjectiveSchema } from "./schemas";
import { updateSubjectiveSummaryTag } from "./subjectiveText";
interface SubjectiveEditorProps {
  onSubmit(v: z.infer<typeof editSubjectiveSchema>): void;
  onCancel(): void;
  isLoading: boolean;
  documentReferences?: fhirTypes.DocumentReference[];
  addendums?: string[];
  prependAddendums?: string[];
  onAutoSave?: (text: string) => Promise<void>;
}
export const SubjectiveEditor = ({
  documentReferences,
  addendums,
  prependAddendums,
  onCancel,
  onSubmit,
  isLoading,
  onAutoSave,
}: SubjectiveEditorProps) => {
  const [previousAddendumsLength, setPreviousAddendumsLength] = useState(0);
  const [previousPrependAddendumsLength, setPreviousPrependAddendumsLength] =
    useState(0);
  const defaultValue = documentReferences
    ?.map((ref) => ref.description)
    .join(" ");

  const form = useForm<z.infer<typeof editSubjectiveSchema>>({
    resolver: zodResolver(editSubjectiveSchema),
    mode: "onSubmit",
    defaultValues: {
      text: defaultValue || "",
    },
  });

  const formRef = useRef<HTMLFormElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Initial load - set base text from documentReferences only
  // Skip reset while user has unsaved typing (form is dirty)
  useEffect(() => {
    if (form.formState.isDirty) return;
    const baseText = (documentReferences || [])
      .map((ref) => updateSubjectiveSummaryTag(ref))
      .filter(Boolean)
      .join("\n\n");
    form.reset({ text: baseText });
    setPreviousAddendumsLength(0);
    setPreviousPrependAddendumsLength(0);
  }, [documentReferences, form]);

  // Append new addendums only when an addendum is added (dupes allowed)
  useEffect(() => {
    if (!addendums) return;

    if (addendums.length > previousAddendumsLength) {
      const newAddendums = addendums.slice(previousAddendumsLength);
      const currentText = form.getValues("text");
      const newText = [currentText, ...newAddendums]
        .filter(Boolean)
        .join("\n\n");

      // shouldDirty: true so autosave detects AI inserts and addendum appends
      form.setValue("text", newText, { shouldDirty: true });
      // Focus the textarea so a subsequent blur (clicking elsewhere, tabbing
      // away) triggers autosave. Without this, focus falls to body when the
      // originating button (e.g. "Add to Subjective") unmounts, and no blur
      // from this textarea ever fires.
      textareaRef.current?.focus();
    }

    setPreviousAddendumsLength(addendums.length);
  }, [addendums, previousAddendumsLength, form]);

  // Prepend new addendums to the top, pushing existing text down
  useEffect(() => {
    if (!prependAddendums) return;

    if (prependAddendums.length > previousPrependAddendumsLength) {
      const newAddendums = prependAddendums.slice(
        previousPrependAddendumsLength,
      );
      const currentText = form.getValues("text");
      const newText = [...newAddendums, currentText]
        .filter(Boolean)
        .join("\n\n");

      form.setValue("text", newText, { shouldDirty: true });
      textareaRef.current?.focus();
    }

    setPreviousPrependAddendumsLength(prependAddendums.length);
  }, [prependAddendums, previousPrependAddendumsLength, form]);

  const handleAutoSaveBlur = async (
    e: React.FocusEvent<HTMLTextAreaElement>,
  ) => {
    // Skip if focus is moving to any element inside the form (Cancel/Save buttons)
    if (formRef.current?.contains(e.relatedTarget as Node | null)) return;
    if (!onAutoSave) return;
    if (!form.formState.isDirty) return;

    const text = form.getValues("text");
    if (!text) return;
    if (form.formState.errors.text) return;

    try {
      await onAutoSave(text);
      form.reset({ text }); // mark clean only after success
    } catch {
      // Leave form dirty so the user has a recovery path via the Save button
    }
  };

  return (
    <form
      ref={formRef}
      onSubmit={form.handleSubmit(onSubmit)}
      className="w-full flex flex-col gap-3 mb-2"
    >
      <Controller
        control={form.control}
        name="text"
        render={({ field, fieldState }) => (
          <Field data-invalid={fieldState.invalid}>
            <Textarea
              ref={textareaRef}
              id="text"
              data-testid="edit-subjective"
              autoFocus
              value={field.value}
              onChange={field.onChange}
              onBlur={(e) => {
                field.onBlur();
                handleAutoSaveBlur(e);
              }}
              rows={6}
              aria-invalid={fieldState.invalid}
            />
            <FieldError errors={[fieldState.error]} />
          </Field>
        )}
      />

      <div className="flex gap-2 ml-auto">
        <Button size="xs" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="xs" type="submit" isLoading={isLoading}>
          Save
        </Button>
      </div>
    </form>
  );
};
